import React, { useState, useEffect } from 'react';
import { Tile, FormGroup, TextInput, Button, Dropdown, Stack, InlineNotification, RadioButtonGroup, RadioButton, InlineLoading } from '@carbon/react';
import { Add } from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import { PowerSource } from '../../../types/IndirectEmissions';

interface EnergySource {
  id: string;
  type: 'grid' | 'ppa' | 'directLink';
  subType: string;
  consumption: string;
  emissionFactor: string;
}

interface CurrentSource {
  id: string;
  type: string;
  subType: string;
  consumption: string;
  emissionFactor: string;
  useStandardFactor: boolean;
  selectedStandardFactor: StandardEmissionFactor | null;
}

interface EnergyTypeOption {
  id: string;
  text: string;
  subTypes: Array<{ id: string; text: string }>;
}

interface StandardEmissionFactor {
  name: string;
  value: number;
  unit: string;
  is_renewable?: boolean;
}

interface DirectTechnicalLinkProps {
  onSourcesAdded: (sources: Array<Omit<PowerSource, 'unit'>>) => void;
}

const DirectTechnicalLink: React.FC<DirectTechnicalLinkProps> = ({ onSourcesAdded }) => {
  const { t } = useTranslation();
  const [currentSource, setCurrentSource] = useState<CurrentSource>({
    id: Date.now().toString(),
    type: '',
    subType: '',
    consumption: '',
    emissionFactor: '',
    useStandardFactor: true,
    selectedStandardFactor: null
  });
  
  const [standardEmissionFactors, setStandardEmissionFactors] = useState<StandardEmissionFactor[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [comparisonMessage, setComparisonMessage] = useState<string | null>(null);

  const energyTypeOptions: EnergyTypeOption[] = [
    {
      id: 'renewable',
      text: t('survey.step5.directLink.energyTypes.renewable'),
      subTypes: [
        { id: 'biomass', text: t('survey.step5.directLink.subTypes.biomass') },
        { id: 'solar', text: t('survey.step5.directLink.subTypes.solar') },
        { id: 'wind', text: t('survey.step5.directLink.subTypes.wind') },
        { id: 'hydro', text: t('survey.step5.directLink.subTypes.hydro') },
      ],
    },
    {
      id: 'fossil',
      text: t('survey.step5.directLink.energyTypes.fossil'),
      subTypes: [
        { id: 'coal', text: t('survey.step5.directLink.subTypes.coal') },
        { id: 'lignite', text: t('survey.step5.directLink.subTypes.lignite') },
        { id: 'naturalGas', text: t('survey.step5.directLink.subTypes.naturalGas') },
        { id: 'petrol', text: t('survey.step5.directLink.subTypes.petrol') },
        { id: 'crudeOil', text: t('survey.step5.directLink.subTypes.crudeOil') },
      ],
    },
    {
      id: 'other',
      text: t('survey.step5.directLink.energyTypes.other'),
      subTypes: [
        { id: 'wasteHeat', text: t('survey.step5.directLink.subTypes.wasteHeat') },
        { id: 'hydrogen', text: t('survey.step5.directLink.subTypes.hydrogen') },
        { id: 'gasworksGas', text: t('survey.step5.directLink.subTypes.gasworksGas') },
        { id: 'cokeovenGas', text: t('survey.step5.directLink.subTypes.cokeovenGas') },
        { id: 'blastfurnaceGas', text: t('survey.step5.directLink.subTypes.blastfurnaceGas') },
        { id: 'industrialWaste', text: t('survey.step5.directLink.subTypes.industrialWaste') },
      ],
    },
  ];

  // Fetch emission factors when the type and subType change
  useEffect(() => {
    const fetchEmissionFactors = async () => {
      if (currentSource.type && currentSource.subType) {
        setLoading(true);
        setError(null);
        
        // Special handling for renewable energy in the frontend
        // We could rely on the backend, but this ensures we always have the proper state
        // even if there's a network issue
        if (currentSource.type === 'renewable') {
          // Set a zero emission factor
          const zeroEmissionFactor = {
            name: `Standard ${currentSource.subType} emission factor`,
            value: 0,
            unit: "tCO2/MWh",
            is_renewable: true
          };
          
          setStandardEmissionFactors([zeroEmissionFactor]);
          setCurrentSource(prev => ({
            ...prev,
            selectedStandardFactor: zeroEmissionFactor,
            emissionFactor: "0",
            useStandardFactor: true // Force standard factor for renewables
          }));
          
          setLoading(false);
          return; // Skip API call for renewables
        }
        
        try {
          // Log the exact API URL for debugging
          const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/emissions/emission-factors/${currentSource.type}/${currentSource.subType}`;
          console.log(`Fetching emission factors from: ${apiUrl}`);
          
          const response = await fetch(apiUrl);
          
          // Log the response status
          console.log(`Response status: ${response.status}`);
          
          if (response.ok) {
            const data = await response.json();
            console.log('Received data:', data);
            
            if (data && data.emission_factors) {
              setStandardEmissionFactors(data.emission_factors);
              
              // Auto-select the first standard factor if available
              if (data.emission_factors.length > 0 && currentSource.useStandardFactor) {
                setCurrentSource(prev => ({
                  ...prev,
                  selectedStandardFactor: data.emission_factors[0],
                  emissionFactor: data.emission_factors[0].value.toString()
                }));
              }
            } else {
              console.log('No emission factors found in the response');
              setStandardEmissionFactors([]);
            }
          } else {
            // Try to get detailed error message
            try {
              const errorData = await response.json();
              throw new Error(`Error ${response.status}: ${errorData.detail || 'Unknown error'}`);
            } catch (jsonError) {
              throw new Error(`Failed to fetch: HTTP ${response.status}`);
            }
          }
        } catch (err) {
          console.error('Error details:', err);
          setError(err instanceof Error ? err.message : 'An unknown error occurred');
          setStandardEmissionFactors([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchEmissionFactors();
  }, [currentSource.type, currentSource.subType]);

  // Compare custom emission factor with standard when it changes
  useEffect(() => {
    if (!currentSource.useStandardFactor && currentSource.emissionFactor && standardEmissionFactors.length > 0) {
      const customFactor = parseFloat(currentSource.emissionFactor);
      const standardFactor = standardEmissionFactors[0].value; // Using first standard factor for comparison
      
      if (customFactor < standardFactor) {
        setComparisonMessage(t('survey.step5.directLink.lowerEmissionFactor'));
      } else if (customFactor > standardFactor) {
        setComparisonMessage(t('survey.step5.directLink.higherEmissionFactor'));
      } else {
        setComparisonMessage(t('survey.step5.directLink.equalEmissionFactor'));
      }
    } else {
      setComparisonMessage(null);
    }
  }, [currentSource.emissionFactor, currentSource.useStandardFactor, standardEmissionFactors, t]);

  const handleDropdownChange = (field: 'type' | 'subType', selectedItem: any): void => {
    if (selectedItem) {
      setCurrentSource(prev => ({
        ...prev,
        [field]: selectedItem.id,
        // Reset related fields when changing energy type
        ...(field === 'type' ? { subType: '', emissionFactor: '', selectedStandardFactor: null } : {}),
        // Reset emission factor when changing subType
        ...(field === 'subType' ? { emissionFactor: '', selectedStandardFactor: null } : {})
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setCurrentSource(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFactorTypeChange = (value: string): void => {
    const useStandard = value === 'standard';
    setCurrentSource(prev => ({
      ...prev,
      useStandardFactor: useStandard,
      // Reset emission factor when switching types
      emissionFactor: useStandard && prev.selectedStandardFactor 
        ? prev.selectedStandardFactor.value.toString() 
        : '',
    }));
  };

  const handleStandardFactorChange = (selectedItem: StandardEmissionFactor | null): void => {
    if (selectedItem) {
      setCurrentSource(prev => ({
        ...prev,
        selectedStandardFactor: selectedItem,
        emissionFactor: selectedItem.value.toString()
      }));
    }
  };

  const handleAddSource = (): void => {
    if (
      currentSource.type &&
      currentSource.subType &&
      currentSource.consumption &&
      currentSource.emissionFactor
    ) {
      const consumption = Number(currentSource.consumption);
      // Add validation for consumption
      if (consumption <= 0) {
        alert(t('survey.step5.validation.consumptionGreaterThanZero'));
        return;
      }
      
      const emissionFactor = Number(currentSource.emissionFactor);
      if (emissionFactor <= 0 && currentSource.type !== 'renewable') {
        // Only validate emission factor > 0 for non-renewables
        alert(t('survey.step5.validation.emissionFactorGreaterThanZero'));
        return;
      }

      const emissions = consumption * emissionFactor;

      // Create the source object according to the PowerSource type
      onSourcesAdded([
        {
          type: 'directLink' as const,
          energyType: currentSource.type,
          subType: currentSource.subType,
          consumption: consumption,
          emissions: emissions,
          // Note: If you want to track which standard factor was used,
          // you would need to add standardFactorName to your PowerSource type
        },
      ]);

      // Reset form
      setCurrentSource({
        id: Date.now().toString(),
        type: '',
        subType: '',
        consumption: '',
        emissionFactor: '',
        useStandardFactor: true,
        selectedStandardFactor: null
      });
      
      // Clear comparison message
      setComparisonMessage(null);
    }
  };

  const getCurrentTypeSubTypes = () => {
    const currentType = energyTypeOptions.find((type) => type.id === currentSource.type);
    return currentType ? currentType.subTypes : [];
  };

  return (
    <Tile className="power-source-content mt-4">
      <Stack gap={4}>
        <h5>{t('survey.step5.directLink.title')}</h5>

        <FormGroup>
          <Stack gap={4}>
            <Dropdown
              id="energy-type"
              titleText={t('survey.step5.directLink.typeLabel')}
              items={energyTypeOptions}
              itemToString={(item: EnergyTypeOption | null) => (item ? item.text : '')}
              selectedItem={energyTypeOptions.find((type) => type.id === currentSource.type)}
              onChange={({ selectedItem }: { selectedItem: EnergyTypeOption | null }) =>
                handleDropdownChange('type', selectedItem)
              }
            />

            {currentSource.type && (
              <Dropdown
                id="energy-subtype"
                titleText={t('survey.step5.directLink.subtypeLabel')}
                items={getCurrentTypeSubTypes()}
                itemToString={(item: { id: string; text: string } | null) =>
                  item ? item.text : ''
                }
                selectedItem={getCurrentTypeSubTypes().find(
                  (subType) => subType.id === currentSource.subType,
                )}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: { id: string; text: string } | null;
                }) => handleDropdownChange('subType', selectedItem)}
              />
            )}

            <TextInput
              id="consumption"
              name="consumption"
              labelText={t('survey.step5.directLink.consumptionLabel')}
              helperText={t('survey.step5.directLink.consumptionHelper')}
              value={currentSource.consumption}
              onChange={handleInputChange}
              type="number"
              placeholder="Enter consumption in MWh"
            />

            {currentSource.type && currentSource.subType && (
              <>
                {/* For renewable energy sources, don't show the radio buttons - always use standard factor (zero) */}
                {currentSource.type === 'renewable' ? (
                  <InlineNotification
                    kind="info"
                    title={t('survey.step5.directLink.renewableEnergyInfo')}
                    subtitle={t('survey.step5.directLink.zeroEmissionFactor')}
                  />
                ) : (
                  <FormGroup legendText={t('survey.step5.directLink.emissionFactorSourceLabel')}>
                    <RadioButtonGroup
                      name="emission-factor-source"
                      orientation="horizontal"
                      valueSelected={currentSource.useStandardFactor ? 'standard' : 'custom'}
                      onChange={handleFactorTypeChange}
                    >
                      <RadioButton
                        id="standard-factor"
                        labelText={t('survey.step5.directLink.useStandardFactor')}
                        value="standard"
                        disabled={standardEmissionFactors.length === 0}
                      />
                      <RadioButton
                        id="custom-factor"
                        labelText={t('survey.step5.directLink.useCustomFactor')}
                        value="custom"
                      />
                    </RadioButtonGroup>
                  </FormGroup>
                )}

                {loading ? (
                  <div>
                    <InlineLoading description={t('survey.step5.directLink.loading')} />
                  </div>
                ) : (
                  <>
                    {/* For renewable energy, always show the zero emission factor */}
                    {currentSource.type === 'renewable' ? (
                      <div className="emission-factor-display">
                        <p>
                          <strong>{t('survey.step5.directLink.emissionFactorLabel')}:</strong> 0 tCO2/MWh
                        </p>
                        <p className="text-sm text-gray-600">
                          {t('survey.step5.directLink.renewableEmissionNote')}
                        </p>
                      </div>
                    ) : currentSource.useStandardFactor ? (
                      standardEmissionFactors.length > 0 ? (
                        <Dropdown
                          id="standard-emission-factor"
                          titleText={t('survey.step5.directLink.standardFactorLabel')}
                          items={standardEmissionFactors}
                          itemToString={(item: StandardEmissionFactor | null) => 
                            item ? `${item.name} (${item.value} ${item.unit})` : ''
                          }
                          selectedItem={currentSource.selectedStandardFactor}
                          onChange={({ selectedItem }: { selectedItem: StandardEmissionFactor | null }) =>
                            handleStandardFactorChange(selectedItem)
                          }
                        />
                      ) : (
                        <InlineNotification
                          kind="warning"
                          title={t('survey.step5.directLink.noStandardFactors')}
                          subtitle={t('survey.step5.directLink.useCustomFactorInstead')}
                        />
                      )
                    ) : (
                      <TextInput
                        id="emissionFactor"
                        name="emissionFactor"
                        labelText={t('survey.step5.directLink.emissionFactorLabel')}
                        helperText={t('survey.step5.directLink.emissionFactorHelper')}
                        value={currentSource.emissionFactor}
                        onChange={handleInputChange}
                        type="number"
                        placeholder="Enter emission factor"
                      />
                    )}
                  </>
                )}

                {error && (
                  <InlineNotification
                    kind="error"
                    title={t('survey.step5.directLink.error')}
                    subtitle={error}
                  />
                )}

                {comparisonMessage && (
                  <InlineNotification
                    kind={comparisonMessage.includes('lower') ? 'info' : 'warning'}
                    title={t('survey.step5.directLink.comparisonTitle')}
                    subtitle={comparisonMessage}
                  />
                )}
              </>
            )}

            <Button
              kind="primary"
              renderIcon={Add}
              onClick={handleAddSource}
              disabled={
                !currentSource.type ||
                !currentSource.subType ||
                !currentSource.consumption ||
                !currentSource.emissionFactor
              }
            >
              {t('survey.step5.grid.addConsumption')}
            </Button>
          </Stack>
        </FormGroup>
      </Stack>
    </Tile>
  );
};

export default DirectTechnicalLink;